"use client"

import { createContext, useCallback, useContext, useRef, useTransition } from "react"

interface CatalogTransitionContextValue {
  isPending: boolean
  pendingView: string | null
  startTransition: (callback: () => void) => void
  startViewTransition: (targetView: string, callback: () => void) => void
}

const CatalogTransitionContext = createContext<CatalogTransitionContextValue>({
  isPending: false,
  pendingView: null,
  startTransition: (cb) => cb(),
  startViewTransition: (_view, cb) => cb(),
})

export function CatalogTransitionProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isPending, startTransition] = useTransition()
  const pendingViewRef = useRef<string | null>(null)

  const startViewTransition = useCallback(
    (targetView: string, callback: () => void) => {
      pendingViewRef.current = targetView
      startTransition(() => {
        callback()
      })
    },
    [startTransition],
  )

  const wrappedStartTransition = useCallback(
    (callback: () => void) => {
      pendingViewRef.current = null
      startTransition(() => {
        callback()
      })
    },
    [startTransition],
  )

  return (
    <CatalogTransitionContext.Provider
      value={{
        isPending,
        pendingView: isPending ? pendingViewRef.current : null,
        startTransition: wrappedStartTransition,
        startViewTransition,
      }}
    >
      {children}
    </CatalogTransitionContext.Provider>
  )
}

export function useCatalogTransition() {
  return useContext(CatalogTransitionContext)
}
